import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Typography,
  Box,
  Grid,
  Button,
  Divider,
  Stack,
  Chip,
  IconButton,
  Paper,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  TextField,
  InputAdornment,
  Tab,
  Tabs,
  LinearProgress,
  Avatar,
  ListItemSecondaryAction,
  Snackbar
} from '@mui/material';
import { 
  Close as CloseIcon, 
  Smartphone as DeviceIcon,
  MyLocation as LocationIcon,
  History as HistoryIcon,
  Info as InfoIcon,
  SystemUpdateAlt as UpdateIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon,
  CheckCircle as SuccessIcon,
  Apps as AppsIcon,
  Search as SearchIcon,
  Android as AndroidIcon,
  Battery90 as BatteryIcon,
  BatteryChargingFull as BatteryChargingIcon,
  Wifi as WifiIcon,
  SignalCellular4Bar as SignalIcon,
  SignalCellularAlt as NetworkIcon,
  PendingActions as PendingIcon,
  CloudDownload as DownloadingIcon,
  CloudDone as InstalledIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  PowerSettingsNew as PowerIcon,
  Business as BusinessIcon,
  QrCode as CodeIcon,
  Badge as BadgeIcon,
  RestartAlt as RestartIcon,
  PhoneAndroid as PhoneIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  AdminPanelSettings as OwnerIcon,
  Home as LauncherIcon,
  HelpOutline as UnknownIcon
} from '@mui/icons-material';
import { Device, InstalledApp, DeviceUpdate, DeviceCommand, CommandType } from '../types';
import { formatLastUpdate } from '../utils/format';
import { isUpdateStalled } from '../utils/updates';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import UpdateApkModal from './UpdateApkModal';
import { useDevices } from '../hooks/useDevices';
import { useDeviceUpdates } from '../hooks/useDeviceUpdates';
import { deviceService } from '../services/deviceService';
import { updateService } from '../services/updateService';
import { useAuth } from '../contexts/AuthContext';

interface DeviceDetailsProps {
  device: Device | null;
  open: boolean;
  onClose: () => void;
  onRequestUpdate: (deviceId: string) => Promise<void>;
}

const DeviceDetails: React.FC<DeviceDetailsProps> = ({ 
  device, 
  open, 
  onClose,
  onRequestUpdate
}) => {
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [forceUpdate, setForceUpdate] = useState(0);
  const [uninstallConfirmOpen, setUninstallConfirmOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState<InstalledApp | null>(null);
  const [uninstalling, setUninstalling] = useState<Record<string, boolean>>({});
  const [uninstallError, setUninstallError] = useState<string | null>(null);
  const [uninstallSuccess, setUninstallSuccess] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editedCustomName, setEditedCustomName] = useState('');
  const [editedCompanyName, setEditedCompanyName] = useState('');
  const [editedCode, setEditedCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [cancelingUpdateId, setCancelingUpdateId] = useState<string | null>(null);
  const [updateActionSuccess, setUpdateActionSuccess] = useState<string | null>(null);
  const [updateActionError, setUpdateActionError] = useState<string | null>(null);
  const [commandLoading, setCommandLoading] = useState<string | null>(null);
  const [commandConfirmOpen, setCommandConfirmOpen] = useState(false);
  const [pendingCommandType, setPendingCommandType] = useState<CommandType | null>(null);
  const [commandSuccess, setCommandSuccess] = useState<string | null>(null);
  const [commandError, setCommandError] = useState<string | null>(null);
  const [recentCommands, setRecentCommands] = useState<DeviceCommand[]>([]);
  const { user } = useAuth();
  const { pendingUpdates, failedUpdates, cancelUpdate } = useDevices(!!user);
  const { updates, loading: updatesLoading } = useDeviceUpdates(device?.id || '', !!user);

  // Observar comandos do dispositivo
  useEffect(() => {
    if (!device?.id || !open) return;
    const unsubscribe = deviceService.observeCommands(device.id, (commands) => {
      setRecentCommands(commands.slice(0, 5));
      // Verificar se algum comando em execução foi completado/falhou
      const lastCommand = commands[0];
      if (lastCommand && commandLoading) {
        if (lastCommand.status === 'completed') {
          setCommandLoading(null);
          setCommandSuccess(getCommandLabel(lastCommand.type) + ' executado com sucesso!');
        } else if (lastCommand.status === 'failed') {
          setCommandLoading(null);
          setCommandError(lastCommand.errorMessage || 'Falha ao executar comando');
        }
      }
    });
    return () => unsubscribe();
  }, [device?.id, open, commandLoading]);

  // Resetar estados quando o dispositivo muda ou o modal é fechado
  useEffect(() => {
    if (!open) {
      setCancelSuccess(false);
      setCancelError(null);
      setTabValue(0);
      setSearchTerm('');
      setUninstallConfirmOpen(false);
      setSelectedApp(null);
      setUninstalling({});
      setUninstallError(null);
      setUninstallSuccess(null);
      setEditMode(false);
      setSaveSuccess(false);
      setSaveError(null);
      setCommandLoading(null);
      setCommandConfirmOpen(false);
      setPendingCommandType(null);
      setCommandSuccess(null);
      setCommandError(null);
    }
  }, [open, device?.id]);

  // Inicializar campos editáveis com valores do dispositivo
  useEffect(() => {
    if (device) {
      setEditedCustomName(device.customName || '');
      setEditedCompanyName(device.companyName || '');
      setEditedCode(device.code || '');
    }
  }, [device]);

  // Este useEffect monitora a mudança de tab para garantir que os dados são carregados corretamente
  useEffect(() => {
    if (tabValue === 2) {
      // Força uma re-renderização quando muda para a aba de histórico
      setForceUpdate(prev => prev + 1);
    }
  }, [tabValue, device?.id]);

  const handleCancelUpdate = async () => {
    if (!device) return;
    
    setCanceling(true);
    setCancelError(null);
    setCancelSuccess(false);
    
    try {
      const success = await cancelUpdate(device.id);
      if (success) {
        setCancelSuccess(true);
        // O Firebase vai notificar automaticamente a mudança através do hook useDevices
      } else {
        setCancelError('Não foi possível encontrar atualizações pendentes');
      }
    } catch (err) {
      setCancelError('Falha ao cancelar atualização do dispositivo');
    } finally {
      setCanceling(false);
    }
  };

  const handleCloseUpdateModal = () => {
    setUpdateModalOpen(false);
  };

  const handleRequestUninstall = (app: InstalledApp) => {
    setSelectedApp(app);
    setUninstallConfirmOpen(true);
  };

  const handleConfirmUninstall = async () => {
    if (!device || !selectedApp) return;

    setUninstalling(prev => ({ ...prev, [selectedApp.packageName]: true }));
    setUninstallError(null);
    setUninstallSuccess(null);

    try {
      await deviceService.requestUninstall(device.id, selectedApp.packageName, selectedApp.appName);
      setUninstallSuccess(`Solicitação de desinstalação enviada para ${selectedApp.appName}`);
      setUninstallConfirmOpen(false);
      setSelectedApp(null);
    } catch (error) {
      setUninstallError(`Erro ao solicitar desinstalação: ${error}`);
    } finally {
      setUninstalling(prev => ({ ...prev, [selectedApp.packageName]: false }));
    }
  };

  const handleCancelUninstallConfirm = () => {
    setUninstallConfirmOpen(false);
    setSelectedApp(null);
  };

  const handleSaveDeviceInfo = async () => {
    if (!device) return;

    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const updateData: { customName?: string; companyName?: string; code?: string } = {};
      
      // Só adiciona o campo se tiver valor ou se precisar limpar (enviar vazio)
      updateData.customName = editedCustomName.trim();
      updateData.companyName = editedCompanyName.trim();
      updateData.code = editedCode.trim();

      await deviceService.updateDeviceInfo(device.id, updateData);

      setSaveSuccess(true);
      setEditMode(false);
      
      // O Firebase vai notificar automaticamente a mudança através do hook useDevices
    } catch (error) {
      setSaveError('Erro ao salvar informações do dispositivo');
      console.error('Erro ao salvar:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleDeviceStatus = async () => {
    if (!device) return;

    setTogglingStatus(true);
    setSaveError(null);

    try {
      const newStatus = device.isActive !== false; // Se undefined ou true, consideramos ativo
      await deviceService.toggleDeviceStatus(device.id, !newStatus);

      setSaveSuccess(true);
      
      // O Firebase vai notificar automaticamente a mudança através do hook useDevices
    } catch (error) {
      setSaveError('Erro ao alterar status do dispositivo');
      console.error('Erro ao alterar status:', error);
    } finally {
      setTogglingStatus(false);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditedCustomName(device?.customName || '');
    setEditedCompanyName(device?.companyName || '');
    setEditedCode(device?.code || '');
    setSaveError(null);
  };

  const getCommandLabel = (type: CommandType): string => {
    switch (type) {
      case 'reboot_device': return 'Reiniciar Dispositivo';
      case 'restart_totem': return 'Reiniciar Totem';
      case 'restart_app': return 'Reiniciar Launcher';
      default: return 'Comando';
    }
  };

  const getCommandDescription = (type: CommandType): string => {
    switch (type) {
      case 'reboot_device': return 'Isso vai reiniciar completamente o dispositivo. O dispositivo ficará offline por alguns minutos.';
      case 'restart_totem': return 'Isso vai fechar e reabrir o aplicativo Indecx Totem no dispositivo.';
      case 'restart_app': return 'Isso vai reiniciar o aplicativo Launcher MDM no dispositivo.';
      default: return '';
    }
  };

  const handleOpenCommandConfirm = (type: CommandType) => {
    setPendingCommandType(type);
    setCommandConfirmOpen(true);
  };

  const handleConfirmCommand = async () => {
    if (!device || !pendingCommandType) return;

    setCommandConfirmOpen(false);
    setCommandLoading(pendingCommandType);
    setCommandError(null);
    setCommandSuccess(null);

    try {
      const sentBy = user ? {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || undefined
      } : undefined;
      await deviceService.sendCommand(device.id, pendingCommandType, sentBy);
    } catch (error) {
      setCommandLoading(null);
      setCommandError(`Erro ao enviar comando: ${error}`);
    }
    setPendingCommandType(null);
  };

  const handleCancelCommand = () => {
    setCommandConfirmOpen(false);
    setPendingCommandType(null);
  };

  const handleCancelSingleUpdate = async (updateId: string) => {
    if (!device) return;

    setCancelingUpdateId(updateId);
    setUpdateActionError(null);
    setUpdateActionSuccess(null);

    try {
      await updateService.cancelUpdate(updateId, device.id);
      setUpdateActionSuccess('Atualização cancelada com sucesso!');
    } catch (error) {
      setUpdateActionError(`Erro ao cancelar atualização: ${error}`);
    } finally {
      setCancelingUpdateId(null);
    }
  };

  if (!device) {
    return null;
  }

  const updateRequestTimeFormatted = device.updateRequestTime 
    ? format(device.updateRequestTime, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
    : 'Nunca';

  const hasPendingUpdate = pendingUpdates[device.id];
  const failedUpdate = failedUpdates[device.id];
  const hasApk = !!device.apkUrl;

  // Usar apenas os aplicativos reais do dispositivo
  const installedApps = device.installedApps || [];

  // Filtrar aplicativos com base no termo de pesquisa
  const filteredApps = searchTerm
    ? installedApps.filter(app => 
        app.appName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.packageName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.versionName.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : installedApps;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="md"
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Stack 
          direction="row" 
          justifyContent="space-between" 
          alignItems="center"
        >
          <Typography variant="h6">
            Detalhes do Dispositivo
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      
      <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Stack 
          direction="row" 
          spacing={1} 
          justifyContent="center"
        >
          <Button
            onClick={() => setTabValue(0)}
            color="primary"
            startIcon={<InfoIcon />}
            variant={tabValue === 0 ? "contained" : "outlined"}
            size="large"
            fullWidth
            sx={{ 
              borderRadius: 2, 
              py: 1,
              ...(tabValue === 0 ? {
                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)'
              } : {})
            }}
          >
            <b>Informações</b>
          </Button>
          <Button
            onClick={() => setTabValue(1)}
            color="primary"
            startIcon={<AppsIcon />}
            variant={tabValue === 1 ? "contained" : "outlined"}
            size="large"
            fullWidth
            sx={{ 
              borderRadius: 2, 
              py: 1,
              ...(tabValue === 1 ? {
                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)'
              } : {})
            }}
          >
            <b>Aplicativos</b>
          </Button>
          <Button
            onClick={() => setTabValue(2)}
            color="primary"
            startIcon={<UpdateIcon />}
            variant={tabValue === 2 ? "contained" : "outlined"}
            size="large"
            fullWidth
            sx={{ 
              borderRadius: 2, 
              py: 1,
              ...(tabValue === 2 ? {
                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)'
              } : {})
            }}
          >
            <b>Histórico</b>
          </Button>
        </Stack>
      </Box>
      
      <DialogContent>
        {tabValue === 0 && (
          <Box sx={{ py: 3 }}>
            <Grid container spacing={3}>
              {/* Cabeçalho */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 2,
                    bgcolor: 'primary.light',
                    color: 'primary.contrastText',
                    borderRadius: 2,
                  }}
                >
                  <Stack 
                    direction={{ xs: 'column', sm: 'row' }} 
                    spacing={3}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                  >
                    <Box
                      sx={{
                        bgcolor: 'primary.main',
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <DeviceIcon sx={{ fontSize: 36 }} />
                    </Box>
                    
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h5" fontWeight="bold" gutterBottom>
                        {device.customName || device.name}
                      </Typography>
                      {device.customName && (
                        <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>
                          Nome Android: {device.name}
                        </Typography>
                      )}
                      <Typography variant="body1">
                        ID: {device.id}
                      </Typography>
                      {device.companyName && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Empresa: {device.companyName}
                        </Typography>
                      )}
                      {device.code && (
                        <Typography variant="body2">
                          Código: {device.code}
                        </Typography>
                      )}
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Chip 
                        label={device.online ? 'Online' : 'Offline'} 
                        color={device.online ? 'success' : 'error'} 
                        variant="filled"
                        size="small"
                      />
                      
                      <Chip 
                        label={device.isActive !== false ? 'Ativo' : 'Desativado'} 
                        color={device.isActive !== false ? 'success' : 'error'} 
                        variant="outlined"
                        size="small"
                      />
                      
                      {hasPendingUpdate && (
                        <Chip 
                          label="Atualização pendente" 
                          color="warning" 
                          variant="filled"
                          size="small"
                        />
                      )}

                      {failedUpdate && (
                        <Chip
                          label="Última atualização falhou"
                          color="error"
                          variant="filled"
                          size="small"
                        />
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>

              {failedUpdate && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <strong>A última atualização enviada falhou.</strong> {failedUpdate.errorMessage}
                    {failedUpdate.updatedAt > 0 && (
                      <> — {format(failedUpdate.updatedAt, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</>
                    )}
                  </Alert>
                </Grid>
              )}

              {/* Informações do dispositivo */}
              <Grid item xs={12} md={6}>
                <Card elevation={1} sx={{ height: '100%' }}>
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" mb={2}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <InfoIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle1" fontWeight="bold">
                          Informações Gerais
                        </Typography>
                      </Stack>
                      {!editMode ? (
                        <Tooltip title="Editar informações">
                          <IconButton onClick={() => setEditMode(true)} size="small" color="primary">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="Salvar">
                            <IconButton onClick={handleSaveDeviceInfo} size="small" color="success" disabled={saving}>
                              {saving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancelar">
                            <IconButton onClick={handleCancelEdit} size="small" color="error">
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      )}
                    </Stack>

                    <Divider sx={{ mb: 2 }} />

                    {saveSuccess && (
                      <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaveSuccess(false)}>
                        Informações salvas com sucesso!
                      </Alert>
                    )}

                    {saveError && (
                      <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
                        {saveError}
                      </Alert>
                    )}

                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Nome do Dispositivo (Android)
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                          {device.name}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Android ID
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {device.androidId || <em style={{ color: '#999' }}>Aguardando envio do dispositivo...</em>}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Número de Série
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {device.serialNumber || <em style={{ color: '#999' }}>Aguardando envio do dispositivo...</em>}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <BadgeIcon fontSize="small" />
                            <span>Nome Personalizado</span>
                          </Stack>
                        </Typography>
                        {editMode ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={editedCustomName}
                            onChange={(e) => setEditedCustomName(e.target.value)}
                            placeholder="Digite um nome personalizado"
                          />
                        ) : (
                          <Typography variant="body1">
                            {device.customName || <em style={{ color: '#999' }}>Não definido</em>}
                          </Typography>
                        )}
                      </Box>

                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <BusinessIcon fontSize="small" />
                            <span>Nome da Empresa</span>
                          </Stack>
                        </Typography>
                        {editMode ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={editedCompanyName}
                            onChange={(e) => setEditedCompanyName(e.target.value)}
                            placeholder="Digite o nome da empresa"
                          />
                        ) : (
                          <Typography variant="body1">
                            {device.companyName || <em style={{ color: '#999' }}>Não definido</em>}
                          </Typography>
                        )}
                      </Box>

                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <CodeIcon fontSize="small" />
                            <span>Código</span>
                          </Stack>
                        </Typography>
                        {editMode ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={editedCode}
                            onChange={(e) => setEditedCode(e.target.value)}
                            placeholder="Digite o código"
                          />
                        ) : (
                          <Typography variant="body1">
                            {device.code || <em style={{ color: '#999' }}>Não definido</em>}
                          </Typography>
                        )}
                      </Box>

                      <Divider />

                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Status do Dispositivo
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                          <Chip 
                            label={device.isActive !== false ? 'Ativo' : 'Desativado'} 
                            color={device.isActive !== false ? 'success' : 'error'} 
                            size="small"
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            color={device.isActive !== false ? 'error' : 'success'}
                            onClick={handleToggleDeviceStatus}
                            disabled={togglingStatus}
                            startIcon={togglingStatus ? <CircularProgress size={16} /> : <PowerIcon />}
                          >
                            {togglingStatus 
                              ? 'Processando...' 
                              : device.isActive !== false ? 'Desativar' : 'Ativar'}
                          </Button>
                        </Stack>
                      </Box>

                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Status de Conexão
                        </Typography>
                        <Typography variant="body1">
                          {device.online ? 'Online' : 'Offline'}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Última Atualização
                        </Typography>
                        <Typography variant="body1">
                          {formatLastUpdate(device.lastUpdate)}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Última Solicitação de Atualização
                        </Typography>
                        <Typography variant="body1">
                          {updateRequestTimeFormatted}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Gerenciamento
                        </Typography>
                        {device.management ? (
                          <>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                              <Chip
                                icon={device.management.isKioskActive ? <LockIcon /> : <LockOpenIcon />}
                                label={device.management.isKioskActive ? 'Kiosk ativo' : 'Kiosk inativo'}
                                color={device.management.isKioskActive ? 'success' : 'error'}
                                size="small"
                              />
                              <Chip
                                icon={<OwnerIcon />}
                                label={device.management.isDeviceOwner ? 'Device Owner' : 'Sem Device Owner'}
                                color={device.management.isDeviceOwner ? 'success' : 'error'}
                                size="small"
                              />
                              <Chip
                                icon={<LauncherIcon />}
                                label={device.management.isDefaultLauncher ? 'Launcher padrão' : 'Não é o launcher padrão'}
                                color={device.management.isDefaultLauncher ? 'success' : 'warning'}
                                size="small"
                                variant="outlined"
                              />
                              {device.management.launcherVersion && (
                                <Chip
                                  label={`Launcher ${device.management.launcherVersion}`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Stack>
                            {!device.management.isDeviceOwner && (
                              <Alert severity="error" sx={{ mt: 1 }}>
                                Sem Device Owner não há kiosk nem atualização remota. Só se
                                recupera presencialmente.
                              </Alert>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                              Reportado {formatLastUpdate(device.management.timestamp)}
                            </Typography>
                          </>
                        ) : (
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                            <Chip
                              icon={<UnknownIcon />}
                              label="Não reportado"
                              size="small"
                              variant="outlined"
                            />
                            <Typography variant="caption" color="text.secondary">
                              Launcher anterior à 1.3.9
                            </Typography>
                          </Stack>
                        )}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card elevation={1} sx={{ height: '100%' }}>
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                      <LocationIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle1" fontWeight="bold">
                        Localização
                      </Typography>
                    </Stack>

                    <Divider sx={{ mb: 2 }} />

                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Latitude
                        </Typography>
                        <Typography variant="body1">
                          {device.location.latitude.toFixed(6)}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Longitude
                        </Typography>
                        <Typography variant="body1">
                          {device.location.longitude.toFixed(6)}
                        </Typography>
                      </Box>

                      <Box sx={{ 
                        height: 100, 
                        bgcolor: 'background.default', 
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center' 
                      }}>
                        <Typography variant="body2" color="text.secondary">
                          Mapa detalhado disponível na visualização principal
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Status do dispositivo */}
              <Grid item xs={12}>
                <Card elevation={1}>
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                      <DeviceIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle1" fontWeight="bold">
                        Status do Dispositivo
                      </Typography>
                      {device.statusBar && (
                        <Tooltip title={`Última atualização: ${format(device.statusBar.timestamp, "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR })}`}>
                          <Chip 
                            label={formatDistanceToNow(device.statusBar.timestamp, { addSuffix: true, locale: ptBR })}
                            size="small" 
                            color="info" 
                            variant="outlined"
                          />
                        </Tooltip>
                      )}
                    </Stack>

                    <Divider sx={{ mb: 2 }} />

                    {!device.statusBar ? (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          Nenhuma informação de status disponível
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          Este dispositivo ainda não reportou informações de status
                        </Typography>
                      </Box>
                    ) : (
                      <Grid container spacing={3}>
                        {/* Bateria */}
                        <Grid item xs={12} sm={4}>
                          <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                                {device.statusBar?.battery?.charging ? (
                                  <BatteryChargingIcon color="success" />
                                ) : (
                                  <BatteryIcon color={(device.statusBar?.battery?.level || 0) > 20 ? "primary" : "error"} />
                                )}
                                <Typography variant="subtitle2">
                                  Bateria
                                </Typography>
                              </Stack>
                              <Divider sx={{ my: 1 }} />
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  Nível: {device.statusBar?.battery?.level || 0}%
                                </Typography>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={device.statusBar?.battery?.level || 0}
                                  color={(device.statusBar?.battery?.level || 0) > 20 ? "primary" : "error"}
                                  sx={{ height: 8, borderRadius: 1 }}
                                />
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                  Status: {device.statusBar?.battery?.charging ? 'Carregando' : 'Não está carregando'}
                                </Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>

                        {/* WiFi */}
                        <Grid item xs={12} sm={4}>
                          <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                                <WifiIcon color={device.statusBar?.wifi?.connected ? "primary" : "disabled"} />
                                <Typography variant="subtitle2">
                                  WiFi
                                </Typography>
                              </Stack>
                              <Divider sx={{ my: 1 }} />
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                  Status: {device.statusBar?.wifi?.connected ? 'Conectado' : 'Desconectado'}
                                </Typography>
                                {device.statusBar?.wifi?.connected && (
                                  <>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                      Rede: {device.statusBar?.wifi?.ssid || ''}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                      Sinal: {device.statusBar?.wifi?.signalStrength || 0}/5
                                    </Typography>
                                    <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                                      {[...Array(5)].map((_, i) => (
                                        <Box 
                                          key={i}
                                          sx={{ 
                                            width: 8, 
                                            height: (i + 1) * 4, 
                                            bgcolor: i < (device.statusBar?.wifi?.signalStrength || 0) ? 'primary.main' : 'grey.300',
                                            borderRadius: 0.5
                                          }} 
                                        />
                                      ))}
                                    </Stack>
                                  </>
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>

                        {/* Rede */}
                        <Grid item xs={12} sm={4}>
                          <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                                <NetworkIcon color={device.statusBar?.network?.connected ? "primary" : "disabled"} />
                                <Typography variant="subtitle2">
                                  Conexão de Dados
                                </Typography>
                              </Stack>
                              <Divider sx={{ my: 1 }} />
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                  Status: {device.statusBar?.network?.connected ? 'Conectado' : 'Desconectado'}
                                </Typography>
                                {device.statusBar?.network?.connected && (
                                  <>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                      Tipo: {device.statusBar?.network?.type || 'Desconhecido'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                      Sinal: {device.statusBar?.network?.signalStrength || 0}/4
                                    </Typography>
                                    <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                                      {[...Array(4)].map((_, i) => (
                                        <Box 
                                          key={i}
                                          sx={{ 
                                            width: 8, 
                                            height: (i + 1) * 5, 
                                            bgcolor: i < (device.statusBar?.network?.signalStrength || 0) ? 'primary.main' : 'grey.300',
                                            borderRadius: 0.5
                                          }} 
                                        />
                                      ))}
                                    </Stack>
                                  </>
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Comandos Remotos */}
              <Grid item xs={12}>
                <Card elevation={1}>
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                      <RestartIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle1" fontWeight="bold">
                        Comandos Remotos
                      </Typography>
                    </Stack>

                    <Divider sx={{ mb: 2 }} />

                    {commandSuccess && (
                      <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCommandSuccess(null)}>
                        {commandSuccess}
                      </Alert>
                    )}

                    {commandError && (
                      <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCommandError(null)}>
                        {commandError}
                      </Alert>
                    )}

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Button
                          variant="outlined"
                          color="warning"
                          fullWidth
                          size="large"
                          startIcon={commandLoading === 'reboot_device' ? <CircularProgress size={20} /> : <RestartIcon />}
                          onClick={() => handleOpenCommandConfirm('reboot_device')}
                          disabled={!!commandLoading || !device.online}
                          sx={{ py: 1.5, borderRadius: 2 }}
                        >
                          {commandLoading === 'reboot_device' ? 'Reiniciando...' : 'Reiniciar Dispositivo'}
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Button
                          variant="outlined"
                          color="info"
                          fullWidth
                          size="large"
                          startIcon={commandLoading === 'restart_totem' ? <CircularProgress size={20} /> : <PhoneIcon />}
                          onClick={() => handleOpenCommandConfirm('restart_totem')}
                          disabled={!!commandLoading || !device.online}
                          sx={{ py: 1.5, borderRadius: 2 }}
                        >
                          {commandLoading === 'restart_totem' ? 'Reiniciando...' : 'Reiniciar Totem'}
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Button
                          variant="outlined"
                          color="secondary"
                          fullWidth
                          size="large"
                          startIcon={commandLoading === 'restart_app' ? <CircularProgress size={20} /> : <RestartIcon />}
                          onClick={() => handleOpenCommandConfirm('restart_app')}
                          disabled={!!commandLoading || !device.online}
                          sx={{ py: 1.5, borderRadius: 2 }}
                        >
                          {commandLoading === 'restart_app' ? 'Reiniciando...' : 'Reiniciar Launcher'}
                        </Button>
                      </Grid>
                    </Grid>

                    {!device.online && (
                      <Alert severity="warning" sx={{ mt: 2 }} icon={<WarningIcon />}>
                        O dispositivo está offline. Os comandos só podem ser enviados quando o dispositivo estiver online.
                      </Alert>
                    )}

                    {/* Histórico de comandos recentes */}
                    {recentCommands.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Comandos Recentes:
                        </Typography>
                        <Stack spacing={0.5}>
                          {recentCommands.map((cmd) => (
                            <Stack
                              key={cmd.id}
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{
                                p: 1,
                                borderRadius: 1,
                                bgcolor: 'background.default'
                              }}
                            >
                              <Chip
                                label={getCommandLabel(cmd.type)}
                                size="small"
                                variant="outlined"
                              />
                              <Chip
                                label={
                                  cmd.status === 'pending' ? 'Pendente' :
                                  cmd.status === 'executing' ? 'Executando' :
                                  cmd.status === 'completed' ? 'Concluído' :
                                  'Falhou'
                                }
                                size="small"
                                color={
                                  cmd.status === 'completed' ? 'success' :
                                  cmd.status === 'failed' ? 'error' :
                                  cmd.status === 'executing' ? 'info' :
                                  'warning'
                                }
                              />
                              <Typography variant="caption" color="text.secondary">
                                {formatDistanceToNow(cmd.createdAt, { addSuffix: true, locale: ptBR })}
                              </Typography>
                              {cmd.sentBy && (
                                <Tooltip title={`Enviado por: ${cmd.sentBy.email}`}>
                                  <Chip
                                    label={cmd.sentBy.displayName || cmd.sentBy.email}
                                    size="small"
                                    variant="outlined"
                                    color="default"
                                    sx={{ fontSize: '0.65rem', height: 20 }}
                                  />
                                </Tooltip>
                              )}
                              {cmd.errorMessage && (
                                <Tooltip title={cmd.errorMessage}>
                                  <ErrorIcon fontSize="small" color="error" />
                                </Tooltip>
                              )}
                            </Stack>
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* APK e Atualizações */}
              <Grid item xs={12}>
                <Card elevation={1}>
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                      <UpdateIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle1" fontWeight="bold">
                        Atualização de APK
                      </Typography>
                    </Stack>

                    <Divider sx={{ mb: 2 }} />

                    {/* Mensagem para usar atualização em massa */}
                    <Alert 
                      severity="info" 
                      icon={<WarningIcon />}
                      sx={{ mb: 2 }}
                    >
                      Para gerenciar atualizações, utilize a funcionalidade de <strong>Atualização em Massa</strong> disponível no painel principal.
                    </Alert>

                    {cancelSuccess && (
                      <Alert 
                        severity="success" 
                        icon={<SuccessIcon />}
                        sx={{ mb: 2 }}
                      >
                        Atualização cancelada com sucesso!
                      </Alert>
                    )}

                    {cancelError && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {cancelError}
                      </Alert>
                    )}

                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={6}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Status da Atualização
                          </Typography>
                          <Typography variant="body1">
                            {hasPendingUpdate 
                              ? 'Atualização pendente'
                              : 'Nenhuma atualização pendente'}
                          </Typography>

                          {cancelSuccess && (
                            <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5 }}>
                              APK removido do dispositivo
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        {hasPendingUpdate ? (
                          <Tooltip title="Cancelar atualização pendente">
                            <Button
                              variant="outlined"
                              color="error"
                              disabled={canceling}
                              onClick={handleCancelUpdate}
                              fullWidth
                              startIcon={canceling ? <CircularProgress size={20} /> : <CancelIcon />}
                            >
                              {canceling ? 'Cancelando...' : 'Cancelar Atualização'}
                            </Button>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary" align="center">
                            {cancelSuccess 
                              ? 'Utilize a opção "Atualização em Massa" para enviar um novo APK'
                              : 'Use a atualização em massa para gerenciar este dispositivo'}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
        
        {tabValue === 1 && (
          <Box sx={{ py: 3 }}>
            {/* Solicitações de desinstalação pendentes */}
            {device.uninstallRequests && Object.keys(device.uninstallRequests).length > 0 && (
              <Card elevation={1} sx={{ mb: 2 }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                    <DeleteIcon color="warning" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Solicitações de Desinstalação Pendentes
                    </Typography>
                    <Chip 
                      label={Object.keys(device.uninstallRequests).length} 
                      size="small" 
                      color="warning" 
                      variant="outlined"
                    />
                  </Stack>
                  
                  <Divider sx={{ mb: 2 }} />
                  
                  <List disablePadding>
                    {Object.entries(device.uninstallRequests).map(([requestId, request]) => (
                      <ListItem key={requestId} sx={{ px: 0 }}>
                        <ListItemIcon>
                          <Avatar sx={{ bgcolor: 'warning.main', width: 32, height: 32 }}>
                            <DeleteIcon fontSize="small" />
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={request.appName}
                          secondary={
                            <>
                              <Typography variant="caption" component="span" display="block">
                                {request.packageName}
                              </Typography>
                              <Typography variant="caption" component="span" display="block">
                                Status: {request.status === 'pending' ? 'Pendente' : request.status}
                              </Typography>
                              <Typography variant="caption" component="span" display="block">
                                Solicitado: {format(request.requestedAt, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                              </Typography>
                            </>
                          }
                        />
                        <Chip 
                          label={request.status === 'pending' ? 'Pendente' : request.status} 
                          size="small" 
                          color={request.status === 'pending' ? 'warning' : 
                                 request.status === 'completed' ? 'success' : 
                                 request.status === 'failed' ? 'error' : 'info'}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}

            <Card elevation={1}>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                  <AppsIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle1" fontWeight="bold">
                    Aplicativos Instalados
                  </Typography>
                  <Chip 
                    label={`${installedApps.length} apps`} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                </Stack>

                <Divider sx={{ mb: 2 }} />

                {installedApps.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhum aplicativo instalado disponível para exibição
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Este dispositivo ainda não reportou informações sobre aplicativos instalados
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {/* Campo de busca */}
                    <TextField
                      fullWidth
                      placeholder="Buscar aplicativos..."
                      variant="outlined"
                      size="small"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      sx={{ mb: 2 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />

                    {/* Lista de aplicativos */}
                    <Box 
                      sx={{ 
                        border: '1px solid', 
                        borderColor: 'divider', 
                        borderRadius: 1,
                        maxHeight: '400px',
                        overflow: 'auto',
                        '&::-webkit-scrollbar': {
                          width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          background: '#f1f1f1',
                          borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: '#888',
                          borderRadius: '4px',
                          '&:hover': {
                            background: '#555',
                          },
                        },
                      }}
                    >
                      <List disablePadding>
                        {filteredApps.length > 0 ? (
                          filteredApps.map((app, index) => (
                                                        <React.Fragment key={app.packageName}>
                              {index > 0 && <Divider component="li" />}
                              <ListItem>
                                <ListItemIcon>
                                  <AndroidIcon color={app.isSystemApp ? "disabled" : "primary"} />
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      {app.appName}
                                      {app.isSystemApp && (
                                        <Chip label="Sistema" size="small" variant="outlined" />
                                      )}
                                    </Box>
                                  }
                                  secondary={
                                    <>
                                      <Typography variant="caption" component="span" display="block">
                                        {app.packageName}
                                      </Typography>
                                      <Typography variant="body2" component="span" color="primary.main" fontWeight="bold">
                                        Versão: {app.versionName} (code: {app.versionCode})
                                      </Typography>
                                      {app.lastUpdated && (
                                        <Typography variant="caption" component="span" display="block">
                                          Atualizado: {format(app.lastUpdated, "dd/MM/yyyy", { locale: ptBR })}
                                        </Typography>
                                      )}
                                    </>
                                  }
                                />
                                {!app.isSystemApp && (
                                  <ListItemSecondaryAction>
                                    <Tooltip title="Desinstalar aplicativo" arrow>
                                      <IconButton
                                        edge="end"
                                        aria-label="uninstall"
                                        onClick={() => handleRequestUninstall(app)}
                                        disabled={uninstalling[app.packageName]}
                                        color="error"
                                        size="small"
                                      >
                                        {uninstalling[app.packageName] ? (
                                          <CircularProgress size={20} />
                                        ) : (
                                          <DeleteIcon />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                  </ListItemSecondaryAction>
                                )}
                              </ListItem>
                            </React.Fragment>
                          ))
                        ) : (
                          <ListItem>
                            <ListItemText 
                              primary={
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary" 
                                  align="center"
                                >
                                  Nenhum aplicativo encontrado
                                </Typography>
                              } 
                            />
                          </ListItem>
                        )}
                      </List>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Box>
        )}
        
        {tabValue === 2 && (
          <Box sx={{ py: 3 }} key={`history-content-${forceUpdate}`}>
            <Card elevation={1}>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                  <UpdateIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle1" fontWeight="bold">
                    Histórico de Atualizações
                  </Typography>
                  {!updatesLoading && (
                    <Chip 
                      label={`${updates.length} ${updates.length === 1 ? 'registro' : 'registros'}`} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  )}
                </Stack>

                <Divider sx={{ mb: 2 }} />

                {updateActionSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }} onClose={() => setUpdateActionSuccess(null)}>
                    {updateActionSuccess}
                  </Alert>
                )}

                {updateActionError && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUpdateActionError(null)}>
                    {updateActionError}
                  </Alert>
                )}

                {updatesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress size={40} />
                  </Box>
                ) : updates.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhum histórico de atualização disponível
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Este dispositivo ainda não possui registros de atualizações
                    </Typography>
                  </Box>
                ) : (
                  <List>
                    {updates.map((update: DeviceUpdate) => {
                      // Determinar o ícone e cor com base no status
                      let statusIcon;
                      let statusColor: 'success' | 'error' | 'warning' | 'info' = 'info';
                      
                      switch (update.status) {
                        case 'installed':
                          statusIcon = <InstalledIcon />;
                          statusColor = 'success';
                          break;
                        case 'installing':
                          statusIcon = <DownloadingIcon />;
                          statusColor = 'info';
                          break;
                        case 'pending':
                          statusIcon = <PendingIcon />;
                          statusColor = 'warning';
                          break;
                        case 'failed':
                          statusIcon = <ErrorIcon />;
                          statusColor = 'error';
                          break;
                        default:
                          statusIcon = <PendingIcon />;
                          statusColor = 'warning';
                      }

                      // Extrair nome do apk da URL
                      const apkName = update.apkUrl ? update.apkUrl.split('/').pop()?.split('_').pop() || 'APK desconhecido' : 'APK desconhecido';
                      
                      return (
                        <React.Fragment key={update.id}>
                          <ListItem
                            alignItems="flex-start"
                            sx={{ 
                              mb: 2,
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              bgcolor: 'background.paper'
                            }}
                          >
                            <ListItemIcon sx={{ mt: 1 }}>
                              <Avatar sx={{ bgcolor: `${statusColor}.main` }}>
                                {statusIcon}
                              </Avatar>
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                  <Typography variant="subtitle1">
                                    {update.updateType === 'newInstall' ? 'Nova Instalação' : 'Atualização'}
                                  </Typography>
                                  <Chip 
                                    label={update.status} 
                                    size="small" 
                                    color={statusColor}
                                  />
                                  {update.deviceId === 'bulk' && (
                                    <Chip 
                                      label="Atualização em massa" 
                                      size="small" 
                                      variant="outlined" 
                                    />
                                  )}
                                </Box>
                              }
                              secondary={
                                <>
                                  <Typography variant="body2" sx={{ mt: 1 }}>
                                    Data: {format(update.createdAt, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                                    <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                      ({formatDistanceToNow(update.createdAt, { addSuffix: true, locale: ptBR })})
                                    </Typography>
                                  </Typography>
                                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    ID: {update.id}
                                  </Typography>
                                  <Typography 
                                    variant="body2" 
                                    component="div" 
                                    sx={{ 
                                      mt: 0.5, 
                                      maxWidth: '100%',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    <Tooltip title={update.apkUrl || 'URL não disponível'} arrow placement="top">
                                      <Box component="span" sx={{ cursor: 'pointer' }}>
                                        APK: {apkName}
                                      </Box>
                                    </Tooltip>
                                  </Typography>
                                  {update.updatedAt !== update.createdAt && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                      Última atualização: {format(update.updatedAt, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                                    </Typography>
                                  )}
                                  {isUpdateStalled(update, device.online) && (
                                    <Alert severity="warning" sx={{ mt: 1 }}>
                                      O dispositivo está online mas não deu sinal de ter
                                      recebido esta atualização. O canal de atualização dele
                                      provavelmente está travado — reinicie o launcher pelos
                                      Comandos Remotos e acompanhe.
                                    </Alert>
                                  )}
                                  {update.status === 'failed' && update.errorMessage && (
                                    <Box sx={{ mt: 1, p: 1, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                                        Erro reportado pelo dispositivo:
                                      </Typography>
                                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                        {update.errorMessage}
                                      </Typography>
                                      {update.errorTimestamp && (
                                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.85 }}>
                                          {format(update.errorTimestamp, "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR })}
                                        </Typography>
                                      )}
                                    </Box>
                                  )}
                                  {(update.status === 'pending' || update.status === 'failed') && (
                                    <Box sx={{ mt: 1 }}>
                                      <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        startIcon={cancelingUpdateId === update.id ? <CircularProgress size={14} /> : <CancelIcon />}
                                        onClick={() => handleCancelSingleUpdate(update.id)}
                                        disabled={cancelingUpdateId === update.id}
                                      >
                                        {cancelingUpdateId === update.id
                                          ? 'Cancelando...'
                                          : update.status === 'pending' ? 'Cancelar Atualização' : 'Remover Registro'}
                                      </Button>
                                    </Box>
                                  )}
                                </>
                              }
                            />
                          </ListItem>
                        </React.Fragment>
                      );
                    })}
                  </List>
                )}
              </CardContent>
            </Card>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Fechar
        </Button>
      </DialogActions>

      {/* Mantemos o modal de atualização por compatibilidade com outras partes do código */}
      <UpdateApkModal
        open={updateModalOpen}
        onClose={handleCloseUpdateModal}
        deviceId={device.id}
      />

      {/* Modal de confirmação de comando remoto */}
      <Dialog
        open={commandConfirmOpen}
        onClose={handleCancelCommand}
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Confirmar {pendingCommandType ? getCommandLabel(pendingCommandType) : 'Comando'}
            </Typography>
            <IconButton onClick={handleCancelCommand} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {pendingCommandType ? getCommandDescription(pendingCommandType) : ''}
          </Alert>
          <Typography variant="body1">
            Tem certeza de que deseja executar este comando no dispositivo "{device?.customName || device?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleConfirmCommand}
            variant="contained"
            color="warning"
          >
            Confirmar
          </Button>
          <Button onClick={handleCancelCommand} variant="outlined">
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmação de desinstalação */}
      <Dialog
        open={uninstallConfirmOpen}
        onClose={handleCancelUninstallConfirm}
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle>
          <Stack 
            direction="row" 
            justifyContent="space-between" 
            alignItems="center"
          >
            <Typography variant="h6">
              Confirmar Desinstalação
            </Typography>
            <IconButton onClick={handleCancelUninstallConfirm} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body1">
            Tem certeza de que deseja desinstalar o aplicativo "{selectedApp?.appName}"?
          </Typography>
        </DialogContent>
        
                 <DialogActions sx={{ px: 3, py: 2 }}>
           <Button 
             onClick={handleConfirmUninstall} 
             variant="contained" 
             color="error"
             disabled={uninstalling[selectedApp?.packageName || '']}
           >
             {uninstalling[selectedApp?.packageName || ''] ? (
               <>
                 <CircularProgress size={16} sx={{ mr: 1 }} />
                 Desinstalando...
               </>
             ) : (
               'Desinstalar'
             )}
           </Button>
           <Button onClick={handleCancelUninstallConfirm} variant="outlined">
             Cancelar
           </Button>
         </DialogActions>
       </Dialog>

       {/* Snackbar para feedback de desinstalação */}
       <Snackbar
         open={!!uninstallSuccess}
         autoHideDuration={5000}
         onClose={() => setUninstallSuccess(null)}
         anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
       >
         <Alert 
           severity="success" 
           sx={{ width: '100%' }}
           onClose={() => setUninstallSuccess(null)}
         >
           {uninstallSuccess}
         </Alert>
       </Snackbar>

       <Snackbar
         open={!!uninstallError}
         autoHideDuration={8000}
         onClose={() => setUninstallError(null)}
         anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
       >
         <Alert 
           severity="error" 
           sx={{ width: '100%' }}
           onClose={() => setUninstallError(null)}
         >
           {uninstallError}
         </Alert>
       </Snackbar>
      </Dialog>
    );
  };

  export default DeviceDetails;